/**
 * ttKit.jsx — building blocks shared by the Titan Treasure panels.
 *
 * Laid out and coloured like the Titan Treasure fraud review pages: white
 * cards with an uppercase eyebrow, a title and a tag; tiles with a tinted
 * icon; findings with a coloured bar; pills in tables. Red is fraud or money
 * lost, amber is waiting or at risk, green is won, clear or prevented, blue
 * is neutral information. Tables, buttons and alerts come from
 * design/primitives.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, DataTable, Skeleton } from "../../design/primitives";
import { count, partyLabel } from "./ttFormat";

export const TONE = {
  red: { fg: "#DC2626", bg: "#FEE2E2" },
  amber: { fg: "#D97706", bg: "#FEF3C7" },
  green: { fg: "#059669", bg: "#D1FAE5" },
  blue: { fg: "#2563EB", bg: "#DBEAFE", text: "#1D44B8" },
  slate: { fg: "#334155", bg: "#E2E8F0" },
};
export const INK = {
  ink: "#0F172A",
  ink2: "#334155",
  muted: "#64748B",
  faint: "#94A3B8",
  line: "#E2E8F0",
  panel: "#FFFFFF",
  page: "#F4F6FB",
  track: "#F1F5F9",
  bar: "#CBD5E1",
};
const GAP = 14;
const panel = { background: INK.panel, border: `1px solid ${INK.line}`, borderRadius: 12, minWidth: 0 };

// Outcome colours, used the same way in every chart.
export const OUTCOME_COLORS = {
  attempts: INK.ink2,
  completed: TONE.green.fg,
  fraud_declines: TONE.red.fg,
  other_declines: TONE.amber.fg,
  declined: TONE.amber.fg,
  blocked: TONE.blue.fg,
  abandoned: INK.bar,
};

export const axisProps = {
  tick: { fontSize: 11, fill: INK.muted },
  tickLine: false,
  axisLine: { stroke: INK.line },
};

// ── Page blocks ─────────────────────────────────────────────────────────────

/** Bold figure inside running text. */
export const B = ({ children }) => <b style={{ color: INK.ink, fontWeight: 600 }}>{children}</b>;

/** Opening summary: white box with a blue left edge. Each child is a paragraph. */
export function Lede({ children }) {
  return (
    <div style={{ ...panel, borderLeft: `3px solid ${TONE.blue.fg}`, padding: "16px 18px", marginBottom: GAP }}>
      {React.Children.toArray(children).map((p, i) => (
        <p key={i} style={{ margin: i ? "10px 0 0" : 0, fontSize: 14, lineHeight: 1.6, color: INK.ink2, maxWidth: "82ch" }}>
          {p}
        </p>
      ))}
    </div>
  );
}

export function Tiles({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 13rem), 1fr))",
        gap: GAP,
        marginBottom: GAP,
      }}
    >
      {children}
    </div>
  );
}

/** Headline number with a tinted icon. `icon` is a lucide component. */
export function Tile({ tone = "blue", icon: Icon, label, value, sub }) {
  const t = TONE[tone];
  return (
    <div style={{ ...panel, padding: "15px 16px", display: "flex", flexDirection: "column", gap: 9 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 32, height: 32, borderRadius: 9, flex: "none",
            display: "grid", placeItems: "center", background: t.bg, color: t.fg,
          }}
        >
          {Icon && <Icon size={16} strokeWidth={1.7} />}
        </span>
        <span style={{ fontSize: 12.5, color: INK.muted, fontWeight: 500, lineHeight: 1.25 }}>{label}</span>
      </div>
      <span
        style={{
          fontSize: 27, fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.05,
          color: INK.ink, fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
      {sub && <span style={{ fontSize: 11.5, color: INK.faint, lineHeight: 1.4 }}>{sub}</span>}
    </div>
  );
}

/** Uppercase label in a card header. */
export function Tag({ tone = "blue", children }) {
  const t = TONE[tone];
  return (
    <span
      style={{
        fontSize: 10.5, letterSpacing: "0.055em", textTransform: "uppercase", fontWeight: 600,
        padding: "4px 9px", borderRadius: 6, whiteSpace: "nowrap", background: t.bg, color: t.text || t.fg,
      }}
    >
      {children}
    </span>
  );
}

/** Card with eyebrow, title, description and an optional tag or controls on the right. */
export function ReviewCard({ eyebrow, title, desc, tag, tagTone = "blue", right, children, style }) {
  return (
    <section style={{ ...panel, padding: 18, marginBottom: GAP, ...style }}>
      <div
        style={{
          display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start",
          justifyContent: "space-between", marginBottom: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          {eyebrow && (
            <p
              style={{
                margin: "0 0 3px", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase",
                color: INK.faint, fontWeight: 600,
              }}
            >
              {eyebrow}
            </p>
          )}
          <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 600, color: INK.ink, overflowWrap: "anywhere" }}>{title}</h3>
          {desc && (
            <p style={{ margin: "5px 0 0", fontSize: 12.5, lineHeight: 1.5, color: INK.muted, maxWidth: "76ch" }}>{desc}</p>
          )}
        </div>
        {(right || tag) && (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {right}
            {tag && <Tag tone={tagTone}>{tag}</Tag>}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

/** Responsive grid: `min` is the narrowest a column may get. */
export function Grid({ min = 320, gap = 16, children, style }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`,
        gap,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Side-by-side ReviewCards; they wrap to one column on narrow screens. */
export function ReviewGrid({ min = 420, children }) {
  return (
    <Grid min={min} gap={`0 ${GAP}px`}>
      {children}
    </Grid>
  );
}

/** One finding: coloured bar, short heading, a sentence or two. */
export function Finding({ tone = "red", title, children, last }) {
  return (
    <div
      style={{
        display: "grid", gridTemplateColumns: "7px minmax(0, 1fr)", gap: 14, alignItems: "stretch",
        padding: last ? "14px 0 0" : "14px 0", borderBottom: last ? "none" : `1px solid ${INK.line}`,
      }}
    >
      <span style={{ borderRadius: 4, background: TONE[tone].fg }} />
      <div>
        <h4 style={{ margin: "0 0 4px", fontSize: 13.5, fontWeight: 600, color: INK.ink }}>{title}</h4>
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: INK.muted, maxWidth: "80ch" }}>{children}</div>
      </div>
    </div>
  );
}

/** Name / value / note rows; `tone` colours the value (green good, red bad). */
export function Metrics({ rows }) {
  return (
    <div>
      {rows.map((r, i) => (
        <div
          key={r.name}
          style={{
            display: "grid", gridTemplateColumns: "1fr auto", gap: "4px 14px", padding: "11px 0",
            borderBottom: i === rows.length - 1 ? "none" : `1px solid ${INK.line}`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: INK.ink2 }}>{r.name}</span>
          <span
            style={{
              fontSize: 16, fontWeight: 600, textAlign: "right", whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums", color: r.tone ? TONE[r.tone].fg : INK.ink,
            }}
          >
            {r.value}
          </span>
          {r.note && (
            <span style={{ gridColumn: "1 / -1", fontSize: 11.5, lineHeight: 1.45, color: INK.faint, maxWidth: "72ch" }}>
              {r.note}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Search, filters and a row count above a table. */
export function Tools({ children, count: shown }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
      {children}
      {shown && <span style={{ fontSize: 12, color: INK.muted, fontVariantNumeric: "tabular-nums" }}>{shown}</span>}
    </div>
  );
}

/** Small print under a card or at the end of a panel. */
export function Foot({ children, style }) {
  return (
    <p style={{ margin: "10px 0 0", fontSize: 11.5, lineHeight: 1.5, color: INK.faint, maxWidth: "78ch", ...style }}>
      {children}
    </p>
  );
}

export function Note({ children, style }) {
  return <div style={{ fontSize: 12.5, color: INK.muted, lineHeight: 1.5, ...style }}>{children}</div>;
}

// ── Tables and table cells ──────────────────────────────────────────────────

/** Border around a table, as on the review pages. */
export function TableFrame({ children }) {
  return <div style={{ border: `1px solid ${INK.line}`, borderRadius: 10, overflow: "hidden" }}>{children}</div>;
}

/** A dense DataTable inside a TableFrame. Takes DataTable's props. */
export function Table({ density = "dense", ...props }) {
  return (
    <TableFrame>
      <DataTable density={density} {...props} />
    </TableFrame>
  );
}

/** Rounded status label in a table cell. */
export function Pill({ tone = "slate", children }) {
  const t = TONE[tone];
  return (
    <span
      style={{
        display: "inline-block", whiteSpace: "nowrap", fontSize: 11, fontWeight: 500,
        padding: "2px 8px", borderRadius: 999, background: t.bg, color: t.fg,
      }}
    >
      {children}
    </span>
  );
}

export const VERDICT_TONES = { HOLD: "red", REVIEW: "amber", OK: "green" };
export function VerdictPill({ verdict }) {
  return verdict ? <Pill tone={VERDICT_TONES[verdict] || "slate"}>{verdict}</Pill> : null;
}

export function PartyPill({ status }) {
  const s = (status || "NONE").toUpperCase();
  const tone = s.includes("BLACKLISTED") ? "red" : s.includes("FORCED_3DS") ? "amber" : s.includes("WHITELISTED") ? "green" : "slate";
  return <Pill tone={tone}>{partyLabel(s)}</Pill>;
}

/** Lets a table cell wrap instead of stretching the table. */
export function Wrap({ children, max = 360 }) {
  return <div style={{ whiteSpace: "normal", maxWidth: max, lineHeight: 1.4, padding: "8px 0" }}>{children}</div>;
}

export const linkButton = {
  background: "none",
  border: "none",
  padding: 0,
  color: TONE.blue.text,
  cursor: "pointer",
  font: "inherit",
  textAlign: "left",
};

/** Inline list of clickable account emails; the default separator reads as "passed to". */
export function AccountLinks({ emails = [], onOpen, max = 360, separator = " → " }) {
  if (!emails.length) return "—";
  return (
    <Wrap max={max}>
      {emails.map((e, i) => (
        <React.Fragment key={e}>
          {i > 0 && <span style={{ color: INK.muted }}>{separator}</span>}
          {onOpen ? (
            <button type="button" onClick={(ev) => { ev.stopPropagation(); onOpen(e); }} style={linkButton}>
              {e}
            </button>
          ) : (
            e
          )}
        </React.Fragment>
      ))}
    </Wrap>
  );
}

// ── Loading, paging, inputs ─────────────────────────────────────────────────

/** Renders a skeleton while loading, an alert on error, else children(data). */
export function LoadState({ state, height = 160, children }) {
  if (state.loading && !state.data) return <Skeleton variant="block" height={height} />;
  if (state.error) {
    return (
      <Alert
        tone={state.error.status === 403 ? "warning" : "danger"}
        title={state.error.status === 403 ? "Not available for this tenant" : "Could not load this panel"}
        description={state.error.message}
      />
    );
  }
  if (!state.data) return null;
  return (
    <div style={{ opacity: state.loading ? 0.6 : 1, transition: "opacity 150ms" }}>{children(state.data)}</div>
  );
}

/** Horizontal bars for a ranked list (top rules, decline reasons). */
export function RankBars({ rows, label, value, barColor = TONE.blue.fg, empty = "Nothing in this window.", format = count }) {
  if (!rows?.length) return <Note>{empty}</Note>;
  const max = Math.max(...rows.map((r) => Number(r[value]) || 0), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => (
        <div key={r[label]} style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) 3fr 56px", gap: 12, alignItems: "center" }}>
          <span title={r[label]} style={{ fontSize: 12.5, color: INK.ink2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r[label]}
          </span>
          <div style={{ height: 10, borderRadius: 6, background: INK.track }}>
            <div style={{ width: `${(Number(r[value]) / max) * 100}%`, height: "100%", borderRadius: 6, background: barColor }} />
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 600, textAlign: "right", fontVariantNumeric: "tabular-nums", color: INK.ink }}>
            {format(r[value])}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Pager({ page, limit, total, onPage }) {
  const pages = Math.max(1, Math.ceil((total || 0) / limit));
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 12 }}>
      <span style={{ fontSize: 12, color: INK.muted }}>
        Page {page} of {count(pages)} · {count(total)} rows
      </span>
      <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Previous
      </Button>
      <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
      </Button>
    </div>
  );
}

const inputStyle = {
  height: 32,
  border: `1px solid ${INK.line}`,
  borderRadius: 8,
  background: INK.panel,
  color: INK.ink,
  fontSize: 12.5,
  fontFamily: "inherit",
};

/** Text input that reports its value after the user pauses typing. */
export function SearchBox({ value, onChange, placeholder, width = 280 }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);
  useEffect(() => {
    if (draft === (value || "")) return undefined;
    const t = setTimeout(() => onChange(draft.trim()), 350);
    return () => clearTimeout(t);
  }, [draft, value, onChange]);
  return (
    <input
      type="search"
      value={draft}
      placeholder={placeholder}
      onChange={(e) => setDraft(e.target.value)}
      style={{ ...inputStyle, width, maxWidth: "100%", padding: "0 11px" }}
    />
  );
}

export function Select({ value, onChange, options, label }) {
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: INK.muted }}>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, padding: "0 8px" }}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Small label/value pair for dense headers. */
export function Fact({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 11.5, color: INK.muted, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13.5, color: INK.ink, overflowWrap: "anywhere" }}>{children}</span>
    </div>
  );
}

/** Wraps a Recharts chart at a fixed height. */
export function ChartBox({ height = 260, children }) {
  return <div style={{ width: "100%", height }}>{children}</div>;
}
